import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Shield, Award } from 'lucide-react';

export default function Footer() {
  return (
    <footer data-testid="footer" className="bg-[#080808] border-t border-white/10">
      {/* NFA Compliance Banner */}
      <div className="bg-orange-500/10 border-b border-orange-500/20">
        <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Shield size={16} className="text-orange-500 flex-shrink-0" />
            <span className="font-inter text-xs text-zinc-400">
              <strong className="text-orange-400">NFA NOTICE:</strong> All suppressor purchases are NFA-regulated items. All NFA rules apply. Must choose UPS shipping.
            </span>
          </div>
          <Link to="/resources/faq" className="font-inter text-xs text-orange-500 hover:text-orange-400 uppercase tracking-widest whitespace-nowrap">
            Learn More →
          </Link>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="lg:col-span-1">
            <div className="mb-6">
              <div className="font-bebas text-3xl text-white tracking-widest">WITT MACHINE</div>
              <div className="font-inter text-[9px] text-zinc-600 tracking-[0.25em] uppercase mt-0.5">& TOOL CO. — TEXAS, USA</div>
            </div>
            <p className="font-inter text-sm text-zinc-500 leading-relaxed mb-6">
              From 42 inches of concrete to 27,000 sq ft of Texas manufacturing — building durable, proven suppressors and muzzle devices since 2012.
            </p>
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-zinc-500">
                <MapPin size={14} className="text-orange-500 flex-shrink-0" />
                <span className="font-inter text-xs">Made in Texas, USA</span>
              </div>
              <div className="flex items-center gap-2 text-zinc-500">
                <Award size={14} className="text-orange-500 flex-shrink-0" />
                <span className="font-inter text-xs">SOT / Class III Manufacturer</span>
              </div>
            </div>
          </div>

          {/* Products */}
          <div>
            <h4 className="font-bebas text-lg text-white tracking-widest mb-4">Products</h4>
            <ul className="space-y-2">
              {[
                { label: 'Suppressors', href: '/suppressors' },
                { label: 'Sound Mitigation Equipment', href: '/sound-mitigation-equipment' },
                { label: 'Clamp-On Muzzle Brakes', href: '/clamp-ons' },
                { label: 'Threaded Muzzle Brakes', href: '/muzzle-devices' },
                { label: 'Shop All', href: '/shop' },
              ].map(item => (
                <li key={item.href}>
                  <Link to={item.href} className="font-inter text-sm text-zinc-500 hover:text-white transition-colors">
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h4 className="font-bebas text-lg text-white tracking-widest mb-4">Resources</h4>
            <ul className="space-y-2">
              {[
                { label: 'Resources Hub', href: '/resources' },
                { label: 'Muzzle Brake FAQ', href: '/resources/faq' },
                { label: 'Measuring Guide', href: '/resources/measuring' },
                { label: 'Installation Instructions', href: '/resources/installation' },
                { label: 'About Us', href: '/about' },
                { label: 'Contact', href: '/contact' },
              ].map(item => (
                <li key={item.href}>
                  <Link to={item.href} className="font-inter text-sm text-zinc-500 hover:text-white transition-colors">
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Dealer & Contact */}
          <div>
            <h4 className="font-bebas text-lg text-white tracking-widest mb-4">Dealer Program</h4>
            <p className="font-inter text-sm text-zinc-500 mb-4 leading-relaxed">
              Are you an FFL dealer? Apply for our wholesale program and grow your product offering with Texas-made quality.
            </p>
            <Link
              to="/dealer-signup"
              data-testid="footer-dealer-cta"
              className="inline-block font-bebas text-sm tracking-widest bg-orange-500 hover:bg-orange-600 text-white px-5 py-2.5 transition-colors"
            >
              APPLY NOW
            </Link>
            <div className="mt-6 space-y-2">
              <a href="https://wittmachine.net/contact/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-zinc-500 hover:text-white transition-colors">
                <Mail size={14} className="text-orange-500 flex-shrink-0" />
                <span className="font-inter text-xs">Contact Us</span>
              </a>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-white/10 mt-12 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="font-inter text-xs text-zinc-600">
            © {new Date().getFullYear()} Witt Machine & Tool Co. All rights reserved. Texas, USA.
          </p>
          <div className="flex items-center gap-6">
            {['Privacy Policy', 'Terms & Conditions', 'Shipping & Returns'].map(item => (
              <Link key={item} to="#" className="font-inter text-xs text-zinc-600 hover:text-zinc-400 transition-colors">
                {item}
              </Link>
            ))}
            <Link
              to="/admin/login"
              data-testid="staff-access-link"
              className="font-inter text-[10px] text-zinc-800 hover:text-zinc-600 transition-colors"
              aria-label="Staff Access"
            >
              Staff Access
            </Link>
            <Link
              to="/dealer/login"
              data-testid="dealer-portal-link"
              className="font-inter text-[10px] text-zinc-800 hover:text-zinc-600 transition-colors"
              aria-label="Dealer Portal"
            >
              Dealer Login
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
